import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

import { InfoApi } from "../../api/InfoApi";
import { OrgAdminApi } from "../../api/OrgAdminApi";
import LeaderboardSlide from "../../components/leaderboardSetting/LeaderboardSlide";
import ReactSwal from "../../helper/AlertHelper";
import { getlocalStorageUserObject } from "../../helper/LocalStorageHelper";
import { parseLeaderboardCode } from "../../helper/leaderboardPreview";
import {
  previewSetting,
  slideIntervalMs,
  slideshowSettings,
} from "../../helper/leaderboardSlide";

const LIST_PATH = "/user/leaderboardSettings";

function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

function errorText(msg) {
  if (typeof msg === "string" && msg) {
    return msg;
  }
  if (typeof msg?.data?.message === "string" && msg.data.message) {
    return msg.data.message;
  }
  return "讀取排行榜失敗";
}

function currentOrgId() {
  try {
    const user = getlocalStorageUserObject();
    return user?.organization_id ?? "";
  } catch {
    return "";
  }
}

function CloseTimer({ onClose }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClose}
      aria-label="關閉"
      title="關閉"
      sx={{
        position: "absolute",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        cursor: "pointer",
        px: 1.5,
        py: 0.75,
        border: 0,
        bgcolor: "transparent",
      }}
    >
      <Box sx={{ width: 56, height: 3, borderRadius: 999, bgcolor: "#3B6FE0" }} />
    </Box>
  );
}

function DisplayMessage({ text, onClose }) {
  return (
    <Box sx={{ position: "relative", minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f7f4ea", px: 3 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#111827", textAlign: "center" }}>
        {text}
      </Typography>
      <CloseTimer onClose={onClose} />
    </Box>
  );
}

export default function UserLeaderboardDisplayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewCode = searchParams.get("code") || "";
  const previewBackground = searchParams.get("background") || "";
  const slideshow = previewCode === "";

  const [settings, setSettings] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [skills, setSkills] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalMs = slideIntervalMs();
  const [remainingMs, setRemainingMs] = useState(intervalMs);
  const remainingRef = useRef(intervalMs);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const playlist = useMemo(() => {
    if (!slideshow) {
      const entry = previewSetting(settings, previewCode, previewBackground);
      return entry ? [entry] : [];
    }
    return slideshowSettings(settings);
  }, [slideshow, settings, previewCode, previewBackground]);

  const playlistKey = playlist.map((item) => `${item.id}:${item.leaderboard_code}`).join("|");
  const safeIndex = playlist.length === 0 ? 0 : index % playlist.length;
  const current = playlist[safeIndex] || null;
  const parsed = parseLeaderboardCode(current?.leaderboard_code);
  const skill = skills.find((item) => item.code === parsed?.skillCode);
  const skillName = skill?.name_zh || skill?.name_en || "";
  const skillUnit = skill?.unit || "";
  const title = current?.title || "";

  console.log("current setting", current);

  useEffect(() => {
    setIndex(0);
  }, [playlistKey]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const requestClose = useCallback(() => {
    if (ReactSwal.isVisible()) {
      return;
    }
    ReactSwal.fire({
      title: "確定要關閉嗎？",
      icon: "warning",
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: "確定",
      cancelButtonText: "取消",
      cancelButtonColor: "#a7a7a7",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      if (window.opener) {
        window.close();
        return;
      }
      navigate(LIST_PATH);
    });
  }, [navigate]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "Escape" || ReactSwal.isVisible()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [requestClose]);

  useEffect(() => {
    let active = true;
    OrgAdminApi.getMyLeaderboardSettingListCallback((isSuccess, msg, data) => {
      if (!active) {
        return;
      }
      setSettingsLoaded(true);
      if (!isSuccess) {
        setSettings([]);
        setSettingsError(typeof msg === "string" && msg ? msg : "讀取排行榜設定失敗");
        return;
      }

      console.log("settings", data);
      setSettingsError("");
      setSettings(Array.isArray(data) ? data : []);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    InfoApi.getSkillList()
      .then((data) => {
        if (active) {
          setSkills(asList(data));
        }
      })
      .catch(() => {
        if (active) {
          setSkills([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!current?.leaderboard_code) {
      return undefined;
    }

    let active = true;
    setRowsLoading(true);
    setRowsError("");
    setRows([]);
    setUpdatedAt(null);
    OrgAdminApi.getLeaderboardPreviewCallback(current.leaderboard_code, (isSuccess, msg, data) => {
      if (!active) {
        return;
      }
      setRowsLoading(false);
      if (!isSuccess) {
        setRows([]);
        setRowsError(errorText(msg));
        return;
      }
      setRows(asList(data));
      setUpdatedAt(new Date());
    });

    return () => {
      active = false;
    };
  }, [current?.leaderboard_code]);

  useEffect(() => {
    remainingRef.current = intervalMs;
    setRemainingMs(intervalMs);
  }, [safeIndex, playlistKey, intervalMs]);

  useEffect(() => {
    if (!slideshow || paused || playlist.length <= 1 || !settingsLoaded) {
      return undefined;
    }

    const deadline = Date.now() + remainingRef.current;
    let advanced = false;
    const timer = window.setInterval(() => {
      if (advanced) {
        return;
      }
      const left = deadline - Date.now();
      if (left <= 0) {
        advanced = true;
        window.clearInterval(timer);
        remainingRef.current = intervalMs;
        setRemainingMs(intervalMs);
        setIndex((currentIndex) => (currentIndex + 1) % playlist.length);
        return;
      }
      remainingRef.current = left;
      setRemainingMs(left);
    }, 250);

    return () => {
      remainingRef.current = Math.max(0, deadline - Date.now());
      window.clearInterval(timer);
    };
  }, [slideshow, paused, playlist.length, settingsLoaded, safeIndex, playlistKey, intervalMs]);

  const move = (delta) => {
    if (playlist.length === 0) {
      return;
    }
    setIndex((currentIndex) => (currentIndex + delta + playlist.length) % playlist.length);
  };

  const togglePause = () => {
    if (!slideshow || playlist.length === 0) {
      return;
    }
    setPaused((value) => !value);
  };

  if (!slideshow && !parseLeaderboardCode(previewCode)) {
    return <DisplayMessage text="排行榜代碼不正確" onClose={requestClose} />;
  }

  if (slideshow && !settingsLoaded) {
    return (
      <Box sx={{ position: "relative", minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f7f4ea" }}>
        <CircularProgress size={32} />
        <CloseTimer onClose={requestClose} />
      </Box>
    );
  }

  if (slideshow && settingsError) {
    return <DisplayMessage text={settingsError} onClose={requestClose} />;
  }

  if (slideshow && playlist.length === 0) {
    return <DisplayMessage text="未有可播放的排行榜" onClose={requestClose} />;
  }

  return (
    <LeaderboardSlide
      code={current?.leaderboard_code || ""}
      background={current?.background || "yellow"}
      rows={rows}
      skillName={skillName}
      skillUnit={skillUnit}
      orgId={currentOrgId()}
      title={title}
      loading={rowsLoading}
      error={rowsError}
      updatedAt={updatedAt}
      dotCount={playlist.length}
      dotIndex={safeIndex}
      remainingMs={remainingMs}
      intervalMs={intervalMs}
      paused={slideshow && paused}
      onPrev={() => move(-1)}
      onNext={() => move(1)}
      onTogglePause={togglePause}
      onClose={requestClose}
    />
  );
}
